import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { TimeRange } from "@dtm-frontend/shared/ui";
import { MILLISECONDS_IN_MINUTE, SKIP_GLOBAL_HTTP_ERRORS_INTERCEPTOR, StringUtils } from "@dtm-frontend/shared/utils";
import { WebsocketService } from "@dtm-frontend/shared/websocket";
import { map } from "rxjs";
import { GEO_ZONES_ENDPOINTS, GeoZonesEndpoints } from "../geo-zones.tokens";
import { AirspaceElementResponseBody, SearchAirspaceElementsRequestBody } from "../models/geo-zones.models";
import {
    DronePointDetailsResponseBody,
    DronePointsResponseBody,
    convertAirspaceElementResponseBodyToAirspaceElement,
    convertDronePointDetailsResponseBodyToDronePointDetails,
    convertDronePointsResponseBodyToDronePoints,
} from "./geo-zones.converter";

const DEFAULT_SPAN_PERIOD_IN_MINUTES = 30;
const DEFAULT_UPPER_LIMIT = 500;
const DEFAULT_AIRSPACE_SEARCH_OPTIONS: SearchAirspaceElementsRequestBody = {
    scope: {
        lowerLimit: 0,
        upperLimit: DEFAULT_UPPER_LIMIT,
        startTime: new Date(),
        endTime: new Date(new Date().getTime() + DEFAULT_SPAN_PERIOD_IN_MINUTES * MILLISECONDS_IN_MINUTE),
    },
    geozones: null,
    includeInformation: false,
    flatten: true,
};
const DRONE_POINT_MAP_ACCEPT_HEADER = "application/vnd.pansa.drone-point-map-item.v1+json";

export type AirspaceElementsSearchOptions = Partial<Omit<SearchAirspaceElementsRequestBody, "scope">> & {
    scope?: Partial<SearchAirspaceElementsRequestBody["scope"]>;
};

@Injectable({
    providedIn: "root",
})
export class GeoZonesApiService {
    constructor(
        private readonly httpClient: HttpClient,
        @Inject(GEO_ZONES_ENDPOINTS) private readonly endpoints: GeoZonesEndpoints,
        private readonly websocketService: WebsocketService
    ) {}

    public searchAirspaceElements(options: AirspaceElementsSearchOptions) {
        const endTime = options.scope?.endTime ?? DEFAULT_AIRSPACE_SEARCH_OPTIONS.scope.endTime;

        const body: SearchAirspaceElementsRequestBody = {
            ...DEFAULT_AIRSPACE_SEARCH_OPTIONS,
            ...options,
            scope: options.scope
                ? {
                      ...DEFAULT_AIRSPACE_SEARCH_OPTIONS.scope,
                      ...options.scope,
                      startTime: options.scope.startTime ?? DEFAULT_AIRSPACE_SEARCH_OPTIONS.scope.startTime,
                      endTime,
                  }
                : DEFAULT_AIRSPACE_SEARCH_OPTIONS.scope,
        };

        const timeRange: TimeRange | undefined =
            options.scope?.startTime && options.scope?.endTime ? { min: options.scope.startTime, max: options.scope.endTime } : undefined;

        return this.httpClient
            .post<AirspaceElementResponseBody>(this.endpoints.searchAirspaceElements, body, {
                context: new HttpContext().set(SKIP_GLOBAL_HTTP_ERRORS_INTERCEPTOR, true),
            })
            .pipe(map((response) => convertAirspaceElementResponseBodyToAirspaceElement(response, endTime, timeRange)));
    }

    public startAirspaceUpdatesWatch() {
        return this.websocketService.watchTopic(this.endpoints.wsAirspaceTopicName);
    }

    public getDronePoints() {
        return this.httpClient
            .get<DronePointsResponseBody>(this.endpoints.getDronePoints, {
                context: new HttpContext().set(SKIP_GLOBAL_HTTP_ERRORS_INTERCEPTOR, true),
                // NOTE: bbox value for Poland
                params: new HttpParams().set("bbox", "14.07,49.00,24.15,54.84"),
                headers: { accept: DRONE_POINT_MAP_ACCEPT_HEADER },
            })
            .pipe(map((response) => convertDronePointsResponseBodyToDronePoints(response)));
    }

    public getDronePointDetails(dronePointId: string) {
        return this.httpClient
            .get<DronePointDetailsResponseBody>(StringUtils.replaceInTemplate(this.endpoints.getDronePointDetails, { id: dronePointId }), {
                context: new HttpContext().set(SKIP_GLOBAL_HTTP_ERRORS_INTERCEPTOR, true),
            })
            .pipe(map((response) => convertDronePointDetailsResponseBodyToDronePointDetails(response, this.endpoints.getDronePointPhoto)));
    }
}
